import { useTabletOrMobile } from '@/hooks/useMediaQuery';
import React, { useContext, useEffect } from 'react';
import { useGetInvoiceCommonData } from '@/hooks/useGetInvoice';
import MobileLayout from './Mobile';
import DesktopLayout from './Desktop';
import { StoreContext } from '@/providers/storeProvider';
import { airTracker } from '@/utils/airTracker';
import { COMPONENT_NAME } from '@/pages/Main';
import { Formik } from 'formik';
import { getCustomerDisplayName } from '@/utils/customer';
import { validateCustomerForm } from '@/utils/validate';

export type CustomerInfoForm = {
  name?: string;
  email?: string;
};
interface IGetLayoutProps {
  enabledPA: boolean;
  isPaid: boolean;
  autoCharge: boolean;
  isVoid: boolean;
}
export enum PageLayout {
  ONE_PAGE = 1,
  TWO_PAGE = 2,
}
const getLayoutProps = ({ enabledPA, isPaid, autoCharge, isVoid }: IGetLayoutProps) => {
  // A3 A4 A5 A6, B3 B7 B8 B9 B10 / B4 B5
  if (!enabledPA || isPaid || isVoid || autoCharge) return PageLayout.ONE_PAGE;
  // A1 A2, B1 B2 B6
  return PageLayout.TWO_PAGE;
};

interface IGetBannerTypeProps {
  isPaid: boolean;
  overdue: boolean;
  isSubscription: boolean;
  autoCharge: boolean;
  isInDunning: boolean;
  isDunningFailed: boolean;
  isVoid: boolean;
}

export enum BannerType {
  NONE = 'NONE',
  PAID = 'PAID',
  OVERDUE = 'OVERDUE',
  SCHEDULED = 'SCHEDULED',
  VOID = 'VOID',
}

const getBannerType = ({
  isPaid,
  overdue,
  isSubscription,
  autoCharge,
  isInDunning,
  isDunningFailed,
  isVoid,
}: IGetBannerTypeProps) => {
  if (isVoid) return BannerType.VOID;
  // A3 A6 B3 B7 B10
  if (isPaid) return BannerType.PAID;
  // A2 A5 B2 B9 B5 B6
  if (overdue) return BannerType.OVERDUE;
  // B4
  if (isSubscription && autoCharge && !isInDunning && !isDunningFailed) return BannerType.SCHEDULED;
  return BannerType.NONE;
};

type LayoutProps = {
  onRendered?: () => void;
};

const Layout = ({ onRendered }: LayoutProps) => {
  const isTabletOrMobile = useTabletOrMobile();
  const { invoiceData } = useContext(StoreContext);
  const { invoice, customerSnapshot } = invoiceData;
  const { isSubscription, overdue, autoCharge, enabledPA, isPaid, isInDunning, isDunningFailed, isVoid } =
    useGetInvoiceCommonData(invoice);
  const customerDisplayName = getCustomerDisplayName(customerSnapshot);
  const customerEmail = customerSnapshot?.email;
  const layout = getLayoutProps({ enabledPA, isPaid, autoCharge, isVoid });
  const bannerType = getBannerType({
    isPaid,
    overdue,
    isSubscription,
    autoCharge,
    isInDunning,
    isDunningFailed,
    isVoid,
  });

  useEffect(() => {
    airTracker.info('layout', {
      component: COMPONENT_NAME,
      isSubscription,
      overdue,
      autoCharge,
      enabledPA,
      isPaid,
      isInDunning,
      isDunningFailed,
      layout,
      bannerType,
      invoiceData,
      isTabletOrMobile,
    });
    onRendered?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRendered]);

  return (
    <Formik<CustomerInfoForm>
      initialValues={{
        name: customerDisplayName || '',
        email: customerEmail || '',
      }}
      validateOnChange={false}
      validateOnBlur={false}
      validate={(values) => validateCustomerForm(values)}
      onSubmit={() => {
        console.log('submit');
      }}
    >
      {isTabletOrMobile ? (
        <MobileLayout layout={layout} bannerType={bannerType} />
      ) : (
        <DesktopLayout layout={layout} bannerType={bannerType} />
      )}
    </Formik>
  );
};

export default Layout;
